import { useState, useEffect } from 'react'
import axios from 'axios'
import { authHeaders } from '../Comisiones'
import styles from './Components.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

const TIPOS_REGLA = [
    { value: 'base',              label: 'Comisión Base (Q1/Q2)' },
    { value: 'escalonada_venta',  label: 'Escalonada por Venta' },
    { value: 'escalonada_recaudo', label: 'Escalonada por Recaudo' },
    { value: 'nuevo_cliente',     label: 'Nuevos Clientes / Comodatos' },
]

const REGLA_VACIA = () => ({
    id: '', nombre: '', tipo: 'escalonada_venta', descripcion: '',
    condicion_recaudo_dias: 20, activa: true,
    niveles: [{ cumplimiento_pct: 50, comision_pct: 0.5 }],
    valor_fijo_cop: '',
    valor_fijo_comodato_cop: '',
})

const BONO_VACIO = () => ({ id: '', nombre: '', umbral_pct: 70, valor_cop: 500000 })
const KPI_VACIO  = () => ({ id: '', descripcion: '', meta: '' })

function NivelesEditor({ niveles, onChange }) {
    const add = () => onChange([...niveles, { cumplimiento_pct: '', comision_pct: '' }])
    const remove = (i) => onChange(niveles.filter((_, idx) => idx !== i))
    const update = (i, field, val) => onChange(niveles.map((n, idx) => idx === i ? { ...n, [field]: val } : n))

    return (
        <div className={styles.nivelesBlock}>
            <div className={styles.nivelesHeader}>
                <span>Niveles de cumplimiento</span>
                <button type="button" className={styles.btnXs} onClick={add}>+ Nivel</button>
            </div>
            {niveles.map((n, i) => (
                <div key={i} className={styles.nivelRow}>
                    <div className={styles.nivelField}>
                        <label>% Cumplimiento meta ≥</label>
                        <input type="number" min="0" max="200" step="5" value={n.cumplimiento_pct}
                            onChange={e => update(i, 'cumplimiento_pct', Number(e.target.value))}
                            className={styles.inputSm} />
                        <span>%</span>
                    </div>
                    <div className={styles.nivelField}>
                        <label>→ Comisión</label>
                        <input type="number" min="0" max="100" step="0.05" value={n.comision_pct}
                            onChange={e => update(i, 'comision_pct', Number(e.target.value))}
                            className={styles.inputSm} />
                        <span>%</span>
                    </div>
                    {niveles.length > 1 && (
                        <button type="button" className={styles.btnRemove} onClick={() => remove(i)}>✕</button>
                    )}
                </div>
            ))}
        </div>
    )
}

function PlanFormModal({ empresaId, periodo, plan, onClose, onSave, setMsg }) {
    const isEdit = !!plan

    const [form, setForm] = useState({
        gestor_email:  plan?.gestor_email  || '',
        gestor_nombre: plan?.gestor_nombre || '',
        region:        plan?.region        || '',
        cargo:         plan?.cargo         || '',
        metas: {
            anual:    plan?.metas?.anual    || '',
            q1:       plan?.metas?.q1       || '',
            q2:       plan?.metas?.q2       || '',
            q3:       plan?.metas?.q3       || '',
            q4:       plan?.metas?.q4       || '',
            periodo:  plan?.metas?.periodo  || '',
        },
        reglas:         plan?.reglas         || [REGLA_VACIA()],
        bonificaciones: plan?.bonificaciones || [BONO_VACIO()],
        kpis:           plan?.kpis           || [],
    })
    const [saving,    setSaving]    = useState(false)
    const [activeSection, setActiveSection] = useState('info')

    // Cuando cambia anual, sugerir distribución por trimestre
    const handleAnualChange = (val) => {
        const total = Number(val) || 0
        setForm(f => ({
            ...f,
            metas: {
                ...f.metas,
                anual: val,
                q1: f.metas.q1 || Math.round(total * 0.17),
                q2: f.metas.q2 || Math.round(total * 0.22),
                q3: f.metas.q3 || Math.round(total * 0.27),
                q4: f.metas.q4 || Math.round(total * 0.34),
                periodo: f.metas.periodo || Math.round(total * 0.22),
            }
        }))
    }

    // Reglas CRUD
    const addRegla = () => setForm(f => ({ ...f, reglas: [...f.reglas, REGLA_VACIA()] }))
    const removeRegla = (i) => setForm(f => ({ ...f, reglas: f.reglas.filter((_, idx) => idx !== i) }))
    const updateRegla = (i, field, val) =>
        setForm(f => ({ ...f, reglas: f.reglas.map((r, idx) => idx === i ? { ...r, [field]: val } : r) }))
    const updateReglaLevels = (i, niveles) =>
        setForm(f => ({ ...f, reglas: f.reglas.map((r, idx) => idx === i ? { ...r, niveles } : r) }))

    // Bonos CRUD
    const addBono = () => setForm(f => ({ ...f, bonificaciones: [...f.bonificaciones, BONO_VACIO()] }))
    const removeBono = (i) => setForm(f => ({ ...f, bonificaciones: f.bonificaciones.filter((_, idx) => idx !== i) }))
    const updateBono = (i, field, val) =>
        setForm(f => ({ ...f, bonificaciones: f.bonificaciones.map((b, idx) => idx === i ? { ...b, [field]: val } : b) }))

    // KPIs CRUD
    const addKpi = () => setForm(f => ({ ...f, kpis: [...f.kpis, KPI_VACIO()] }))
    const removeKpi = (i) => setForm(f => ({ ...f, kpis: f.kpis.filter((_, idx) => idx !== i) }))
    const updateKpi = (i, field, val) =>
        setForm(f => ({ ...f, kpis: f.kpis.map((k, idx) => idx === i ? { ...k, [field]: val } : k) }))

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            const headers = await authHeaders()
            const payload = {
                ...form,
                periodo,
                metas: Object.fromEntries(
                    Object.entries(form.metas).map(([k, v]) => [k, Number(v) || 0])
                ),
            }
            const enc = encodeURIComponent(form.gestor_email)
            await axios.put(`${API}/comisiones/${empresaId}/planes/${periodo}/${enc}`, payload, { headers })
            onSave()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally {
            setSaving(false)
        }
    }

    const SECTIONS = [
        { id: 'info',   label: '👤 Gestor' },
        { id: 'metas',  label: '🎯 Metas' },
        { id: 'reglas', label: `📏 Reglas (${form.reglas.length})` },
        { id: 'bonos',  label: `🏅 Bonos (${form.bonificaciones.length})` },
        { id: 'kpis',   label: `📌 KPIs (${form.kpis.length})` },
    ]

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalLarge} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>{isEdit ? '✏️ Editar Plan' : '➕ Nuevo Plan de Comisión'} — {periodo}</h3>
                    <button className={styles.modalClose} onClick={onClose}>✕</button>
                </div>

                {/* Secciones nav */}
                <div className={styles.modalSectionsNav}>
                    {SECTIONS.map(s => (
                        <button key={s.id} type="button"
                            className={`${styles.sectionNavBtn} ${activeSection === s.id ? styles.sectionNavActive : ''}`}
                            onClick={() => setActiveSection(s.id)}>
                            {s.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className={styles.modalBody}>

                    {/* ── Información del gestor ── */}
                    {activeSection === 'info' && (
                        <div className={styles.formGrid2}>
                            <div className={styles.formGroup}>
                                <label>Email del gestor *</label>
                                <input id="plan-gestor-email" type="email" required
                                    value={form.gestor_email}
                                    onChange={e => setForm(f => ({ ...f, gestor_email: e.target.value }))}
                                    disabled={isEdit}
                                    placeholder="gestor@empresa.com"
                                    className={styles.input} />
                                {isEdit && <small className={styles.hint}>El email no puede cambiarse</small>}
                            </div>
                            <div className={styles.formGroup}>
                                <label>Nombre completo</label>
                                <input type="text" value={form.gestor_nombre}
                                    onChange={e => setForm(f => ({ ...f, gestor_nombre: e.target.value }))}
                                    placeholder="Nombre Apellido" className={styles.input} />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Región</label>
                                <input type="text" value={form.region}
                                    onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                                    placeholder="Ej: Nor-Occidente, Santanderes…" className={styles.input} />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Cargo</label>
                                <input type="text" value={form.cargo}
                                    onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))}
                                    placeholder="Ej: Gestor Comercial" className={styles.input} />
                            </div>
                        </div>
                    )}

                    {/* ── Metas ── */}
                    {activeSection === 'metas' && (
                        <div>
                            <p className={styles.hint}>Define las metas de ventas. "Meta período" es la que se usa para calcular cumplimiento en el período seleccionado.</p>
                            <div className={styles.formGrid2}>
                                {[
                                    { key: 'anual',   label: 'Meta Anual', onChange: handleAnualChange },
                                    { key: 'periodo', label: `Meta ${periodo}` },
                                    { key: 'q1',      label: 'Meta Q1' },
                                    { key: 'q2',      label: 'Meta Q2' },
                                    { key: 'q3',      label: 'Meta Q3' },
                                    { key: 'q4',      label: 'Meta Q4' },
                                ].map(({ key, label, onChange }) => (
                                    <div key={key} className={styles.formGroup}>
                                        <label>{label} (COP)</label>
                                        <input type="number" min="0" step="1000"
                                            value={form.metas[key]}
                                            onChange={e => {
                                                if (onChange) onChange(e.target.value)
                                                else setForm(f => ({ ...f, metas: { ...f.metas, [key]: e.target.value } }))
                                            }}
                                            placeholder="0" className={styles.input} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Reglas ── */}
                    {activeSection === 'reglas' && (
                        <div>
                            {form.reglas.map((r, i) => (
                                <div key={i} className={styles.reglaBlock}>
                                    <div className={styles.reglaBHeader}>
                                        <span className={styles.reglaNum}>Regla {i + 1}</span>
                                        <div className={styles.reglaBadgeRow}>
                                            <span className={`${styles.tipoBadge} ${styles[`tipo_${r.tipo}`]}`}>
                                                {TIPOS_REGLA.find(t => t.value === r.tipo)?.label || r.tipo}
                                            </span>
                                            <label className={styles.toggleSwitch}>
                                                <input type="checkbox" checked={r.activa}
                                                    onChange={e => updateRegla(i, 'activa', e.target.checked)} />
                                                <span className={styles.toggleSlider}></span>
                                                <span className={styles.toggleLabel}>{r.activa ? 'Activa' : 'Inactiva'}</span>
                                            </label>
                                        </div>
                                        {form.reglas.length > 1 && (
                                            <button type="button" className={styles.btnRemoveBlock} onClick={() => removeRegla(i)}>🗑️</button>
                                        )}
                                    </div>
                                    <div className={styles.formGrid2}>
                                        <div className={styles.formGroup}>
                                            <label>Nombre de la regla *</label>
                                            <input type="text" value={r.nombre} required
                                                onChange={e => updateRegla(i, 'nombre', e.target.value)}
                                                placeholder="Ej: Comisión Base Q1" className={styles.input} />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>Tipo</label>
                                            <select value={r.tipo} className={styles.input}
                                                onChange={e => updateRegla(i, 'tipo', e.target.value)}>
                                                {TIPOS_REGLA.map(t => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {r.tipo === 'escalonada_recaudo' && (
                                            <div className={styles.formGroup}>
                                                <label>Días máx. para recaudo</label>
                                                <input type="number" min="1" max="90" value={r.condicion_recaudo_dias}
                                                    onChange={e => updateRegla(i, 'condicion_recaudo_dias', Number(e.target.value))}
                                                    className={styles.inputSm} />
                                            </div>
                                        )}
                                        {r.tipo === 'nuevo_cliente' && (
                                            <>
                                                <div className={styles.formGroup}>
                                                    <label>Valor fijo por cliente (COP)</label>
                                                    <input type="number" min="0" value={r.valor_fijo_cop}
                                                        onChange={e => updateRegla(i, 'valor_fijo_cop', e.target.value)}
                                                        placeholder="0" className={styles.input} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label>Valor fijo por comodato (COP)</label>
                                                    <input type="number" min="0" value={r.valor_fijo_comodato_cop || ''}
                                                        onChange={e => updateRegla(i, 'valor_fijo_comodato_cop', e.target.value)}
                                                        placeholder="0" className={styles.input} />
                                                </div>
                                            </>
                                        )}
                                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                            <label>Descripción (opcional)</label>
                                            <input type="text" value={r.descripcion}
                                                onChange={e => updateRegla(i, 'descripcion', e.target.value)}
                                                placeholder="Describe la regla..." className={styles.input} />
                                        </div>
                                    </div>
                                    {r.tipo !== 'nuevo_cliente' && (
                                        <NivelesEditor
                                            niveles={r.niveles || []}
                                            onChange={(nvs) => updateReglaLevels(i, nvs)}
                                        />
                                    )}
                                </div>
                            ))}
                            <button type="button" id="btn-add-regla" className={styles.btnAddBlock} onClick={addRegla}>
                                ➕ Agregar regla
                            </button>
                        </div>
                    )}

                    {/* ── Bonificaciones ── */}
                    {activeSection === 'bonos' && (
                        <div>
                            <p className={styles.hint}>Bonos adicionales no prestacionales por superar umbrales de cumplimiento.</p>
                            {form.bonificaciones.map((b, i) => (
                                <div key={i} className={styles.bonoRow}>
                                    <div className={styles.formGroup}>
                                        <label>Nombre del bono</label>
                                        <input type="text" value={b.nombre}
                                            onChange={e => updateBono(i, 'nombre', e.target.value)}
                                            placeholder="Ej: Bono Q3 por cumplimiento" className={styles.input} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Umbral (% meta) ≥</label>
                                        <input type="number" min="0" max="200" step="5" value={b.umbral_pct}
                                            onChange={e => updateBono(i, 'umbral_pct', Number(e.target.value))}
                                            className={styles.inputSm} />
                                        <span>%</span>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Valor (COP)</label>
                                        <input type="number" min="0" value={b.valor_cop}
                                            onChange={e => updateBono(i, 'valor_cop', Number(e.target.value))}
                                            className={styles.input} />
                                    </div>
                                    {form.bonificaciones.length > 1 && (
                                        <button type="button" className={styles.btnRemoveBlock} onClick={() => removeBono(i)}>🗑️</button>
                                    )}
                                </div>
                            ))}
                            <button type="button" className={styles.btnAddBlock} onClick={addBono}>➕ Agregar bono</button>
                        </div>
                    )}

                    {/* ── KPIs ── */}
                    {activeSection === 'kpis' && (
                        <div>
                            <p className={styles.hint}>Indicadores de desempeño que se monitorean (no calculados automáticamente).</p>
                            {form.kpis.map((k, i) => (
                                <div key={i} className={styles.kpiRow}>
                                    <div className={styles.formGroup} style={{ flex: 2 }}>
                                        <label>Descripción del KPI</label>
                                        <input type="text" value={k.descripcion}
                                            onChange={e => updateKpi(i, 'descripcion', e.target.value)}
                                            placeholder="Ej: 4 visitas presenciales por día" className={styles.input} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Meta</label>
                                        <input type="text" value={k.meta}
                                            onChange={e => updateKpi(i, 'meta', e.target.value)}
                                            placeholder="Ej: 4 por día" className={styles.input} />
                                    </div>
                                    <button type="button" className={styles.btnRemoveBlock} onClick={() => removeKpi(i)}>🗑️</button>
                                </div>
                            ))}
                            <button type="button" className={styles.btnAddBlock} onClick={addKpi}>➕ Agregar KPI</button>
                        </div>
                    )}

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btnGhost} onClick={onClose}>Cancelar</button>
                        <button type="submit" id="btn-guardar-plan" className={styles.btnPrimary} disabled={saving}>
                            {saving ? 'Guardando...' : '💾 Guardar plan'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default PlanFormModal
